// The project explorer: a dense VS Code-style file tree with real folders.
// Folders collapse/expand and can be created and nested; each .sl contract
// carries its .args.json companion as a nested child. New / rename happen inline
// (never a window.prompt). Right-click a file, a folder, OR the empty panel to
// open a context menu of the relevant actions.

import {
  useCallback,
  useMemo,
  useState,
  type JSX,
  type MouseEvent as ReactMouseEvent,
} from "react";
import {
  ChevronRight,
  ChevronsDownUp,
  Copy,
  CornerDownRight,
  FilePlus2,
  FolderPlus,
  PencilLine,
  SquarePen,
  Trash2,
} from "lucide-react";
import { argsPathFor, useIde, type VFile } from "../store";
import { NEW_CONTRACT_TEMPLATE } from "../fs/examples";
import { FileGlyph, glyphClass, kindOf } from "./icons";
import {
  buildFileTree,
  splitExt,
  validateSegment,
  withBsExt,
  type TreeFolderNode,
  type TreeNode,
} from "./tree";
import { ConfirmDialog, ContextMenu, InlineEdit, rowPad, type MenuAction } from "./parts";

interface MenuState {
  /** Target path, or null for the empty-panel menu. */
  target: string | null;
  isFolder: boolean;
  x: number;
  y: number;
}

type EditState =
  | { mode: "new-contract"; parent: string }
  | { mode: "new-file"; parent: string }
  | { mode: "new-folder"; parent: string }
  | { mode: "rename"; path: string; isFolder: boolean }
  | null;

function parentOf(path: string): string {
  const i = path.lastIndexOf("/");
  return i < 0 ? "" : path.slice(0, i);
}
function segOf(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1);
}

export function Explorer(): JSX.Element {
  const files = useIde((s) => s.files);
  const folders = useIde((s) => s.folders);
  const activePath = useIde((s) => s.activePath);
  const openFile = useIde((s) => s.openFile);
  const newFile = useIde((s) => s.newFile);
  const renameFile = useIde((s) => s.renameFile);
  const deleteFile = useIde((s) => s.deleteFile);
  const newFolder = useIde((s) => s.newFolder);
  const renameFolder = useIde((s) => s.renameFolder);
  const deleteFolder = useIde((s) => s.deleteFolder);

  const tree = useMemo(() => buildFileTree(files, folders), [files, folders]);
  const isEmpty = tree.length === 0;

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [edit, setEdit] = useState<EditState>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [confirm, setConfirm] = useState<{ path: string; isFolder: boolean } | null>(null);

  const expand = useCallback((path: string) => {
    if (!path) return;
    setCollapsed((c) => {
      if (!c.has(path)) return c;
      const next = new Set(c);
      next.delete(path);
      return next;
    });
  }, []);

  const toggleFolder = useCallback((path: string) => {
    setCollapsed((c) => {
      const next = new Set(c);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const startCreate = useCallback(
    (mode: "new-contract" | "new-file" | "new-folder", parent: string) => {
      setMenu(null);
      expand(parent);
      setEdit({ mode, parent });
    },
    [expand],
  );

  const commitNewContract = useCallback(
    (parent: string, raw: string) => {
      const seg = withBsExt(raw);
      if (validateSegment(seg, parent, files, folders)) return;
      const full = parent ? `${parent}/${seg}` : seg;
      newFile(full, NEW_CONTRACT_TEMPLATE);
      const argsPath = argsPathFor(full);
      if (!files[argsPath]) newFile(argsPath, "{}\n");
      openFile(full);
      setEdit(null);
    },
    [files, folders, newFile, openFile],
  );

  const commitNewFile = useCallback(
    (parent: string, raw: string) => {
      const seg = raw.trim();
      if (validateSegment(seg, parent, files, folders)) return;
      newFile(parent ? `${parent}/${seg}` : seg, "");
      setEdit(null);
    },
    [files, folders, newFile],
  );

  const commitNewFolder = useCallback(
    (parent: string, raw: string) => {
      const seg = raw.trim();
      if (validateSegment(seg, parent, files, folders)) return;
      const full = parent ? `${parent}/${seg}` : seg;
      newFolder(full);
      expand(full);
      setEdit(null);
    },
    [files, folders, newFolder, expand],
  );

  const commitRename = useCallback(
    (path: string, isFolder: boolean, raw: string) => {
      const parent = parentOf(path);
      const seg = raw.trim();
      const to = parent ? `${parent}/${seg}` : seg;
      if (to === path) {
        setEdit(null);
        return;
      }
      if (validateSegment(seg, parent, files, folders, segOf(path))) return;
      if (isFolder) {
        renameFolder(path, to);
      } else {
        renameFile(path, to);
        // Keep a .sl file's args companion alongside it.
        if (path.endsWith(".sl")) {
          const fromArgs = argsPathFor(path);
          if (files[fromArgs]) renameFile(fromArgs, argsPathFor(to));
        }
      }
      setEdit(null);
    },
    [files, folders, renameFile, renameFolder],
  );

  const duplicate = useCallback(
    (path: string) => {
      const src = files[path];
      if (!src) return;
      const dir = parentOf(path);
      const seg = segOf(path);
      const { base, ext } = splitExt(seg);
      let cand = `${base}-copy${ext}`;
      let n = 2;
      while (files[dir ? `${dir}/${cand}` : cand]) cand = `${base}-copy-${n++}${ext}`;
      newFile(dir ? `${dir}/${cand}` : cand, src.content);
    },
    [files, newFile],
  );

  const doDelete = useCallback(
    (path: string) => {
      if (folders.includes(path)) {
        deleteFolder(path);
      } else {
        deleteFile(path);
        if (path.endsWith(".sl")) {
          const argsPath = argsPathFor(path);
          if (files[argsPath]) deleteFile(argsPath);
        }
      }
    },
    [deleteFile, deleteFolder, folders, files],
  );

  // ---- context menus ----
  const fileMenu = useCallback(
    (path: string): MenuAction[] => [
      { id: "open", label: "Open", icon: <CornerDownRight size={14} strokeWidth={1.75} />, run: () => { openFile(path); setMenu(null); } },
      { id: "rename", label: "Rename", icon: <PencilLine size={14} strokeWidth={1.75} />, shortcut: "F2", run: () => { setEdit({ mode: "rename", path, isFolder: false }); setMenu(null); } },
      { id: "duplicate", label: "Duplicate", icon: <Copy size={14} strokeWidth={1.75} />, run: () => { duplicate(path); setMenu(null); } },
      { id: "delete", label: "Delete", icon: <Trash2 size={14} strokeWidth={1.75} />, shortcut: "⌫", danger: true, separated: true, run: () => { setConfirm({ path, isFolder: false }); setMenu(null); } },
    ],
    [openFile, duplicate],
  );

  const folderMenu = useCallback(
    (path: string): MenuAction[] => [
      { id: "new-contract", label: "New Contract", icon: <FilePlus2 size={14} strokeWidth={1.75} />, run: () => startCreate("new-contract", path) },
      { id: "new-file", label: "New File", icon: <FilePlus2 size={14} strokeWidth={1.75} />, run: () => startCreate("new-file", path) },
      { id: "new-folder", label: "New Folder", icon: <FolderPlus size={14} strokeWidth={1.75} />, run: () => startCreate("new-folder", path) },
      { id: "rename", label: "Rename", icon: <PencilLine size={14} strokeWidth={1.75} />, shortcut: "F2", separated: true, run: () => { setEdit({ mode: "rename", path, isFolder: true }); setMenu(null); } },
      { id: "delete", label: "Delete", icon: <Trash2 size={14} strokeWidth={1.75} />, shortcut: "⌫", danger: true, run: () => { setConfirm({ path, isFolder: true }); setMenu(null); } },
    ],
    [startCreate],
  );

  const panelMenu = useCallback(
    (): MenuAction[] => [
      { id: "new-contract", label: "New Contract", icon: <FilePlus2 size={14} strokeWidth={1.75} />, run: () => startCreate("new-contract", "") },
      { id: "new-file", label: "New File", icon: <FilePlus2 size={14} strokeWidth={1.75} />, run: () => startCreate("new-file", "") },
      { id: "new-folder", label: "New Folder", icon: <FolderPlus size={14} strokeWidth={1.75} />, run: () => startCreate("new-folder", "") },
    ],
    [startCreate],
  );

  const onContextMenu = useCallback(
    (e: ReactMouseEvent, target: string | null, isFolder: boolean) => {
      e.preventDefault();
      e.stopPropagation();
      setMenu({ target, isFolder, x: e.clientX, y: e.clientY });
    },
    [],
  );

  const menuActions = (m: MenuState): MenuAction[] =>
    m.target === null ? panelMenu() : m.isFolder ? folderMenu(m.target) : fileMenu(m.target);

  // ---- create-row helper ----
  const renderCreate = (parent: string, depth: number): JSX.Element | null => {
    if (!edit || edit.mode === "rename" || edit.parent !== parent) return null;
    if (edit.mode === "new-folder") {
      return (
        <InlineEdit
          kind="folder"
          initial=""
          depth={depth}
          validate={(v) => validateSegment(v.trim(), parent, files, folders)}
          onCommit={(v) => commitNewFolder(parent, v)}
          onCancel={() => setEdit(null)}
        />
      );
    }
    if (edit.mode === "new-contract") {
      return (
        <InlineEdit
          kind="seal"
          initial=""
          depth={depth}
          validate={(v) => validateSegment(withBsExt(v), parent, files, folders)}
          onCommit={(v) => commitNewContract(parent, v)}
          onCancel={() => setEdit(null)}
        />
      );
    }
    return (
      <InlineEdit
        kind="plain"
        initial=""
        depth={depth}
        validate={(v) => validateSegment(v.trim(), parent, files, folders)}
        onCommit={(v) => commitNewFile(parent, v)}
        onCancel={() => setEdit(null)}
      />
    );
  };

  // ---- a single file row (also used for the nested args companion) ----
  const renderFileRow = (file: VFile, depth: number): JSX.Element => {
    if (edit && edit.mode === "rename" && !edit.isFolder && edit.path === file.path) {
      return (
        <InlineEdit
          key={`edit-${file.path}`}
          kind={kindOf(file)}
          initial={segOf(file.path)}
          depth={depth}
          selectBase
          validate={(v) => validateSegment(v.trim(), parentOf(file.path), files, folders, segOf(file.path))}
          onCommit={(v) => commitRename(file.path, false, v)}
          onCancel={() => setEdit(null)}
        />
      );
    }

    const kind = kindOf(file);
    const { base, ext } = splitExt(segOf(file.path));
    const isActive = activePath === file.path;

    return (
      <div
        key={file.path}
        role="treeitem"
        aria-selected={isActive}
        aria-level={depth + 1}
        tabIndex={0}
        className={`exp-row${isActive ? " is-active" : ""}`}
        style={{ paddingLeft: rowPad(depth) }}
        title={file.path}
        onClick={() => openFile(file.path)}
        onDoubleClick={() => setEdit({ mode: "rename", path: file.path, isFolder: false })}
        onContextMenu={(e) => onContextMenu(e, file.path, false)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openFile(file.path); }
          else if (e.key === "F2") { e.preventDefault(); setEdit({ mode: "rename", path: file.path, isFolder: false }); }
          else if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); setConfirm({ path: file.path, isFolder: false }); }
        }}
      >
        <span className="exp-row-twist" aria-hidden="true" />
        <span className={glyphClass(kind)} aria-hidden="true">
          <FileGlyph kind={kind} />
        </span>
        <span className="exp-row-name">
          {base}
          {ext && <span className="exp-row-ext">{ext}</span>}
        </span>
        {rowActions(file.path, false)}
      </div>
    );
  };

  const renderFolderRow = (node: TreeFolderNode, depth: number): JSX.Element => {
    if (edit && edit.mode === "rename" && edit.isFolder && edit.path === node.path) {
      return (
        <InlineEdit
          key={`edit-${node.path}`}
          kind="folder"
          initial={node.name}
          depth={depth}
          validate={(v) => validateSegment(v.trim(), parentOf(node.path), files, folders, node.name)}
          onCommit={(v) => commitRename(node.path, true, v)}
          onCancel={() => setEdit(null)}
        />
      );
    }
    const open = !collapsed.has(node.path);
    return (
      <div key={node.path} role="group" className="exp-folder">
        <div
          role="treeitem"
          aria-expanded={open}
          aria-level={depth + 1}
          tabIndex={0}
          className="exp-row exp-row--folder"
          style={{ paddingLeft: rowPad(depth) }}
          title={node.path}
          onClick={() => toggleFolder(node.path)}
          onContextMenu={(e) => onContextMenu(e, node.path, true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleFolder(node.path); }
            else if (e.key === "F2") { e.preventDefault(); setEdit({ mode: "rename", path: node.path, isFolder: true }); }
            else if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); setConfirm({ path: node.path, isFolder: true }); }
          }}
        >
          <span className="exp-row-twist" aria-hidden="true">
            <ChevronRight size={12} strokeWidth={2} className={`exp-twisty${open ? " is-open" : ""}`} />
          </span>
          <span className="exp-glyph exp-glyph--folder" aria-hidden="true">
            <FileGlyph kind="folder" open={open} />
          </span>
          <span className="exp-row-name">{node.name}</span>
          {rowActions(node.path, true)}
        </div>
        {open && (
          <div role="group">
            {renderCreate(node.path, depth + 1)}
            {node.children.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderNode = (node: TreeNode, depth: number): JSX.Element => {
    if (node.type === "folder") return renderFolderRow(node, depth);
    return (
      <div key={node.file.path} role="group">
        {renderFileRow(node.file, depth)}
        {node.args && renderFileRow(node.args, depth + 1)}
      </div>
    );
  };

  // small inline pieces reused by file + folder rows
  function rowActions(path: string, isFolder: boolean): JSX.Element {
    return (
      <span className="exp-row-actions">
        <button type="button" className="exp-row-action" tabIndex={-1} title="Rename" aria-label={`Rename ${path}`}
          onClick={(e) => { e.stopPropagation(); setEdit({ mode: "rename", path, isFolder }); }}>
          <SquarePen size={13} strokeWidth={1.75} />
        </button>
        <button type="button" className="exp-row-action exp-row-action--danger" tabIndex={-1} title="Delete" aria-label={`Delete ${path}`}
          onClick={(e) => { e.stopPropagation(); setConfirm({ path, isFolder }); }}>
          <Trash2 size={13} strokeWidth={1.75} />
        </button>
      </span>
    );
  }

  return (
    <div className="exp">
      <ExplorerHeader
        onNewContract={() => startCreate("new-contract", "")}
        onNewFolder={() => startCreate("new-folder", "")}
        onCollapseAll={
          isEmpty ? undefined : () => setCollapsed(new Set(folders))
        }
      />

      {isEmpty && !edit ? (
        <div className="exp-body" onContextMenu={(e) => onContextMenu(e, null, false)}>
          <EmptyState onNew={() => startCreate("new-contract", "")} />
        </div>
      ) : (
        <div
          className="exp-body"
          role="tree"
          aria-label="Workspace files"
          onContextMenu={(e) => {
            // only the empty area (not a row) opens the panel menu
            if (e.target === e.currentTarget) onContextMenu(e, null, false);
          }}
        >
          {renderCreate("", 0)}
          {tree.map((node) => renderNode(node, 0))}
        </div>
      )}

      {menu && (
        <ContextMenu x={menu.x} y={menu.y} actions={menuActions(menu)} onClose={() => setMenu(null)} />
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm.isFolder ? `Delete folder “${segOf(confirm.path)}”?` : `Delete “${segOf(confirm.path)}”?`}
          message={
            confirm.isFolder
              ? "This folder and everything inside it will be permanently removed."
              : "This file will be permanently removed."
          }
          danger
          onConfirm={() => {
            doDelete(confirm.path);
            setConfirm(null);
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

function ExplorerHeader(props: {
  onNewContract: () => void;
  onNewFolder: () => void;
  onCollapseAll?: () => void;
}): JSX.Element {
  return (
    <header className="exp-header">
      <span className="label-caps exp-header-title">Explorer</span>
      <div className="exp-header-actions">
        <button type="button" className="exp-header-btn" title="New contract" aria-label="New contract" onClick={props.onNewContract}>
          <FilePlus2 size={15} strokeWidth={1.75} />
        </button>
        <button type="button" className="exp-header-btn" title="New folder" aria-label="New folder" onClick={props.onNewFolder}>
          <FolderPlus size={15} strokeWidth={1.75} />
        </button>
        <button type="button" className="exp-header-btn" title="Collapse all" aria-label="Collapse all folders" disabled={!props.onCollapseAll} onClick={props.onCollapseAll}>
          <ChevronsDownUp size={15} strokeWidth={1.75} />
        </button>
      </div>
    </header>
  );
}

function EmptyState(props: { onNew: () => void }): JSX.Element {
  return (
    <div className="exp-empty">
      <span className="exp-empty-icon" aria-hidden="true">
        <FolderPlus size={22} strokeWidth={1.5} />
      </span>
      <div className="exp-empty-title">No files yet</div>
      <p className="exp-empty-text">
        Create your first Seal contract to compile it to a taproot address.
      </p>
      <button type="button" className="exp-empty-action" onClick={props.onNew}>
        <FilePlus2 size={14} strokeWidth={1.75} />
        New contract
      </button>
    </div>
  );
}
